import ConditionResolver from '@/Modules/Quote/Components/QuoteForm/UiRules/Conditions/ConditionResolver.js'
import UiRule from '@/Modules/Quote/Components/QuoteForm/UiRules/UiRule.js'
import { OPERATORS } from '@/Modules/Quote/Components/QuoteForm/UiRules/utils.js'
import UiRuleAction from '@/Modules/Quote/Components/QuoteForm/UiRules/Actions/UiRuleAction.js'
import * as math from 'mathjs'

const DEFAULT_PRECISION = 2

export default class Calculate extends UiRuleAction {
  constructor(baseNode) {
    super(baseNode, new ConditionResolver(baseNode))
  }

  static common() {
    return {
      uuidV4RegexForPath: new RegExp(
        /.[A-F\d]{8}-[A-F\d]{4}-4[A-F\d]{3}-[89AB][A-F\d]{3}-[A-F\d]{12}./i
      ),
    }
  }

  static PARAMETER_FUNCTION_MAP = {
    sumMultiple: {
      parser: 'sumMultiple',
      mathFunction: 'sum',
    },
    maxMultiple: {
      parser: 'maxMultiple',
      mathFunction: 'max',
    },
  }

  /**
   *
   * @param {object} rule
   * @returns {object} baseNode
   */
  execute(rule) {
    const uiRule = new UiRule(rule)

    if (uiRule.condition && !this.resolver.evaluateCondition(uiRule)) {
      return this.baseNode
    }

    const parser = math.parser()

    const paramFn = Object.keys(Calculate.PARAMETER_FUNCTION_MAP).find((key) =>
      rule.parameters.startsWith(`${key}(`)
    )

    if (paramFn) {
      const mappedFn = Calculate.PARAMETER_FUNCTION_MAP[paramFn]
      parser.set(mappedFn.parser, (property) => {
        const path = this.resolver.getPathToNode(property)

        const rowSourceProperty = path
          ? _.last(path.split(Calculate.common().uuidV4RegexForPath) || [''])
          : ''
        const [node] = property.split('.rows')

        if (node && rowSourceProperty && rowSourceProperty.length > 0) {
          const nodePath = node.replace(/\./g, '.properties.')
          const rows = _.get(this.baseNode.properties, `${nodePath}.rows`)
          const values = _.map(rows, (row) => {
            const value = parseFloat(_.get(row, rowSourceProperty + '.value'))
            return isNaN(value) ? 0 : value
          }).join(',')

          if (values) {
            return parser.evaluate(`${mappedFn.mathFunction}(${values})`)
          }

          return 0
        }

        return 0
      })
    }

    const scope = this.getCalculationScope(rule)
    const precision = this.getCalculationPrecision(rule)

    let snakedFormula = paramFn
      ? rule.parameters
      : this.prepareCalculateParams(rule.parameters)
          .map((expressionItem, index) => {
            if (
              OPERATORS.includes(expressionItem) ||
              !isNaN(Number(expressionItem))
            ) {
              return expressionItem
            }
            return expressionItem + '.' + index
          })
          .toString()
          .replace(/\,/g, ' ')

    _.each(scope, (value, key) => {
      snakedFormula = snakedFormula.replace(key, _.camelCase(key))
      parser.set(_.camelCase(key), value)
    })

    let value = parser.evaluate(snakedFormula, scope)
    value = math.round(value, precision)

    this.resolver.setValueToNode(rule.target, value, rule.trigger)

    return this.baseNode
  }

  prepareCalculateParams(parameters) {
    if (!parameters) return parameters
    return parameters
      .replace(/\)/g, ' ) ')
      .replace(/\(/g, ' ( ')
      .split(' ')
      .filter((item) => item && item.length > 0)
  }

  getCalculationScope(rule) {
    const expression = this.prepareCalculateParams(rule.parameters)
    return (
      expression &&
      expression.reduce((scope, expressionItem, index) => {
        if (
          OPERATORS.includes(expressionItem) ||
          !isNaN(Number(expressionItem))
        ) {
          return scope
        }
        const value = this.resolver.getValue(expressionItem)
        if (value !== undefined) {
          const numericValue = parseFloat(value)
          const key = expressionItem + '.' + index
          scope[key] = isNaN(numericValue) ? 0 : numericValue
        }
        return scope
      }, {})
    )
  }

  /**
   *
   * @param {object} rule
   * @returns {number}
   */
  getCalculationPrecision(rule) {
    const uiOptions = this.resolver.getUiOptions(rule.target)

    return isNaN(uiOptions?.precision) ? DEFAULT_PRECISION : uiOptions.precision
  }
}
